import Navbar from "../componenets/Navbar";
import Sidebar from "../componenets/Sidebar";
import React, { useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const role = localStorage.getItem("role");
const API_BASE = "https://www.globizsystems.com/";

const AddCustomer = () => {
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState("");

  const chooseFile = (key) => (event) => {
    const selected = event.target.files?.[0] || null;
    setFiles((current) => ({ ...current, [key]: selected }));
  };

  const authConfig = () => ({
    headers: {
      accessToken: localStorage.getItem("accessToken"),
    },
  });

  const requireFile = (key, xlsxOnly = false) => {
    const selected = files[key];
    if (!selected) {
      swal("Choose a file", "Please select an Excel file first.", "warning");
      return null;
    }

    const lower = selected.name.toLowerCase();
    const valid = xlsxOnly
      ? lower.endsWith(".xlsx")
      : lower.endsWith(".xlsx") || lower.endsWith(".xls");

    if (!valid) {
      swal(
        "Wrong file type",
        xlsxOnly
          ? "This master-data upload only accepts the .xlsx files created for the system."
          : "Please select an Excel .xlsx or .xls file.",
        "error"
      );
      return null;
    }

    return selected;
  };

  const formatValidationMessage = (data) => {
    const errors = Array.isArray(data?.errors) ? data.errors : [];
    if (!errors.length) return data?.message || "The file could not be uploaded.";

    const details = errors
      .slice(0, 12)
      .map((item) => {
        const row = item.row > 0 ? `Row ${item.row}` : "File";
        return `${row} - ${item.field}: ${item.issue}`;
      })
      .join("\n");

    const more = data.errorCount > 12 ? `\n\n...and ${data.errorCount - 12} more issue(s).` : "";
    return `${data.message || "Please correct the Excel file."}\n\n${details}${more}`;
  };

  const showRequestError = (error) => {
    const data = error?.response?.data;
    if (data?.code === "INVALID_UPLOAD_FILE") {
      swal({
        title: "Excel file needs changes",
        text: formatValidationMessage(data),
        icon: "error",
      });
      return;
    }

    swal({
      title: "Upload failed",
      text: data?.message || error?.message || "Unable to upload the file.",
      icon: "error",
    });
  };

  const makeFormData = (selected, extra = {}) => {
    const formData = new FormData();
    formData.append("file", selected);
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== undefined && value !== null) formData.append(key, value);
    });
    return formData;
  };

  const uploadSimpleExcel = async (event, key, endpoint, successMessage) => {
    event.preventDefault();
    const selected = requireFile(key, false);
    if (!selected) return;

    try {
      setBusy(key);
      const response = await axios.post(
        `${API_BASE}${endpoint}`,
        makeFormData(selected),
        authConfig()
      );
      localStorage.setItem("customers", JSON.stringify(response.data));
      await swal("Success", successMessage, "success");
    } catch (error) {
      showRequestError(error);
    } finally {
      setBusy("");
    }
  };

  const buildCustomerConfirmation = (preview) => {
    const warning = preview?.warnings?.[0];
    const lines = [
      `Dataset: ${String(preview.type || "").toUpperCase()} / ${preview.template}`,
      `Current records: ${preview.currentRows}`,
      `New file records: ${preview.incomingRows}`,
      `Old records to remove: ${preview.recordsThatWillBeRemoved}`,
      "",
      "Only records for this AIR/SEWA + Perumahan/Penjaja dataset will be replaced.",
    ];

    if (warning?.severe) {
      lines.push(
        "",
        "WARNING: The new file is less than half the size of the current dataset. Check that it is a complete file before continuing."
      );
    }

    return lines.join("\n");
  };

  const uploadCustomerMaster = async (event, key, type) => {
    event.preventDefault();
    const selected = requireFile(key, true);
    if (!selected) return;

    const endpoint = `${API_BASE}/uploads/customers/upload-excel`;

    try {
      setBusy(key);

      // Pass 1: complete validation. Database is not modified.
      const validation = await axios.post(
        endpoint,
        makeFormData(selected, { type, action: "validate" }),
        authConfig()
      );

      const preview = validation.data;
      const confirmed = await swal({
        title: "File valid - replace old customers?",
        text: buildCustomerConfirmation(preview),
        icon: preview?.warnings?.some((item) => item.severe) ? "warning" : "info",
        buttons: {
          cancel: "Cancel",
          confirm: {
            text: "Replace Customers",
            value: true,
          },
        },
        dangerMode: true,
      });

      if (!confirmed) return;

      // Pass 2: backend validates the same file again inside the replacement flow.
      const result = await axios.post(
        endpoint,
        makeFormData(selected, { type, action: "replace" }),
        authConfig()
      );

      const data = result.data;
      await swal({
        title: "Customers updated",
        text: `${String(data.type || type).toUpperCase()} / ${data.template}\nImported: ${data.importedRows}\nRemoved old records: ${data.removedOldRows}`,
        icon: "success",
      });
    } catch (error) {
      showRequestError(error);
    } finally {
      setBusy("");
    }
  };

  const buildKawasanConfirmation = (preview) => {
    const categories = Array.isArray(preview?.categories) ? preview.categories : [];
    const lines = categories.map(
      (item) =>
        `${item.script}/${String(item.type).toUpperCase()}: ${item.currentRows} current -> ${item.incomingRows} new`
    );

    if (preview?.warnings?.some((item) => item.severe)) {
      lines.push(
        "",
        "WARNING: At least one category is being reduced by more than 50%. Make sure this is a complete kawasan file."
      );
    }

    lines.push("", "Categories not present in this file will not be changed.");
    return lines.join("\n");
  };

  const uploadKawasanMaster = async (event) => {
    event.preventDefault();
    const key = "kawasan";
    const selected = requireFile(key, true);
    if (!selected) return;

    const endpoint = `${API_BASE}/uploads/kawasan/upload-excel`;

    try {
      setBusy(key);
      const validation = await axios.post(
        endpoint,
        makeFormData(selected, { action: "validate" }),
        authConfig()
      );

      const preview = validation.data;
      const confirmed = await swal({
        title: "File valid - replace old kawasan?",
        text: buildKawasanConfirmation(preview),
        icon: preview?.warnings?.some((item) => item.severe) ? "warning" : "info",
        buttons: {
          cancel: "Cancel",
          confirm: {
            text: "Replace Kawasan",
            value: true,
          },
        },
        dangerMode: true,
      });

      if (!confirmed) return;

      const result = await axios.post(
        endpoint,
        makeFormData(selected, { action: "replace" }),
        authConfig()
      );

      const data = result.data;
      await swal({
        title: "Kawasan updated",
        text: `Imported: ${data.importedRows}\nRemoved old records: ${data.removedOldRows}`,
        icon: "success",
      });
    } catch (error) {
      showRequestError(error);
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <Navbar />
      <div className="row">
        <Sidebar />
      </div>

      {role === "1" && (
        <>
          <main className="main-content position-relative h-100 border-radius-md contained ">
            <div className="container-fluid g-3 ">
              <h4 className="colorfix sizefix mx-2">Update Customers Air</h4>
              <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
                <div className="container-fluid" style={{ backgroundColor: "white" }}>
                  <div className="container-fluid px-0 ">
                    <div className="col-12">
                      <div className="container-fluid pb-4 pt-3 g-3">
                        <div className="row g-0">
                          <form onSubmit={(event) => uploadCustomerMaster(event, "airCustomers", "air")}>
                            <input
                              className="col-6"
                              type="file"
                              accept=".xlsx"
                              onChange={chooseFile("airCustomers")}
                            />
                            <button className="col-2" type="submit" disabled={busy === "airCustomers"}>
                              {busy === "airCustomers" ? "Checking..." : "Validate & Upload"}
                            </button>
                          </form>
                          <small className="mt-2 text-muted">
                            Required columns: KAWASAN_ID, KAWASAN_SEWAAN, NAMA, NO_TELEFON_1,
                            NO_TELEFON_2, no_nic, NO_UNIT, NO_HARTA, NO_AKAUN_AIR, status, template.
                            Phone numbers must not start with 0 or 60.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>

          <main className="main-content position-relative h-100 border-radius-md contained ">
            <div className="container-fluid g-3 ">
              <h4 className="colorfix sizefix mx-2">Update Customers Sewa</h4>
              <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
                <div className="container-fluid" style={{ backgroundColor: "white" }}>
                  <div className="container-fluid px-0 ">
                    <div className="col-12">
                      <div className="container-fluid pb-4 pt-3 g-3">
                        <div className="row g-0">
                          <form onSubmit={(event) => uploadCustomerMaster(event, "sewaCustomers", "sewa")}>
                            <input
                              className="col-6"
                              type="file"
                              accept=".xlsx"
                              onChange={chooseFile("sewaCustomers")}
                            />
                            <button className="col-2" type="submit" disabled={busy === "sewaCustomers"}>
                              {busy === "sewaCustomers" ? "Checking..." : "Validate & Upload"}
                            </button>
                          </form>
                          <small className="mt-2 text-muted">
                            Uses the same standardized customer format. The template column decides
                            Perumahan or Penjaja; this upload replaces only that SEWA dataset.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}

      <main className="main-content position-relative h-100 border-radius-md contained ">
        <div className="container-fluid g-3 ">
          <h4 className="colorfix sizefix mx-2">Add Bills</h4>
          <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
            <div className="container-fluid" style={{ backgroundColor: "white" }}>
              <div className="container-fluid px-0 ">
                <div className="col-12">
                  <div className="container-fluid pb-4 pt-3 g-3">
                    <div className="row g-0">
                      <form onSubmit={(event) => uploadSimpleExcel(event, "bills", "/uploads/upload_bills", "Bills Uploaded")}>
                        <input className="col-6" type="file" accept=".xlsx,.xls" onChange={chooseFile("bills")} />
                        <button className="col-2" type="submit" disabled={busy === "bills"}>Upload</button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <main className="main-content position-relative h-100 border-radius-md contained ">
        <div className="container-fluid g-3 ">
          <h4 className="colorfix sizefix mx-2">Add Sewa Bills</h4>
          <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
            <div className="container-fluid" style={{ backgroundColor: "white" }}>
              <div className="container-fluid px-0 ">
                <div className="col-12">
                  <div className="container-fluid pb-4 pt-3 g-3">
                    <div className="row g-0">
                      <form onSubmit={(event) => uploadSimpleExcel(event, "sewaBills", "/uploads/upload_sewa_bills", "Sewa Bills Uploaded")}>
                        <input className="col-6" type="file" accept=".xlsx,.xls" onChange={chooseFile("sewaBills")} />
                        <button className="col-2" type="submit" disabled={busy === "sewaBills"}>Upload</button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <main className="main-content position-relative h-100 border-radius-md contained ">
        <div className="container-fluid g-3 ">
          <h4 className="colorfix sizefix mx-2">Add Outstanding Bills</h4>
          <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
            <div className="container-fluid" style={{ backgroundColor: "white" }}>
              <div className="container-fluid px-0 ">
                <div className="col-12">
                  <div className="container-fluid pb-4 pt-3 g-3">
                    <div className="row g-0">
                      <form onSubmit={(event) => uploadSimpleExcel(event, "outstanding", "/uploads/upload_outstanding_bills", "Outstanding Bills Uploaded")}>
                        <input className="col-6" type="file" accept=".xlsx,.xls" onChange={chooseFile("outstanding")} />
                        <button className="col-2" type="submit" disabled={busy === "outstanding"}>Upload</button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <main className="main-content position-relative h-100 border-radius-md contained ">
        <div className="container-fluid g-3 ">
          <h4 className="colorfix sizefix mx-2">Add Followups</h4>
          <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
            <div className="container-fluid" style={{ backgroundColor: "white" }}>
              <div className="container-fluid px-0 ">
                <div className="col-12">
                  <div className="container-fluid pb-4 pt-3 g-3">
                    <div className="row g-0">
                      <form onSubmit={(event) => uploadSimpleExcel(event, "followup", "/uploads/upload_followup", "Followups Uploaded")}>
                        <input className="col-6" type="file" accept=".xlsx,.xls" onChange={chooseFile("followup")} />
                        <button className="col-2" type="submit" disabled={busy === "followup"}>Upload</button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <main className="main-content position-relative h-100 border-radius-md contained ">
        <div className="container-fluid g-3 ">
          <h4 className="colorfix sizefix mx-2">Update Kawasan</h4>
          <div className="card px-2 pt-3 pb-3 mb-4 cardbg">
            <div className="container-fluid" style={{ backgroundColor: "white" }}>
              <div className="container-fluid px-0 ">
                <div className="col-12">
                  <div className="container-fluid pb-4 pt-3 g-3">
                    <div className="row g-0">
                      <form onSubmit={uploadKawasanMaster}>
                        <input className="col-6" type="file" accept=".xlsx" onChange={chooseFile("kawasan")} />
                        <button className="col-2" type="submit" disabled={busy === "kawasan"}>
                          {busy === "kawasan" ? "Checking..." : "Validate & Upload"}
                        </button>
                      </form>
                      <small className="mt-2 text-muted">
                        Required worksheet: Kawasan Upload. Columns: kawasan_id, kawasan_name,
                        script, type. Only the script/type categories included in the file are replaced.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AddCustomer;
